<template>
    <modal @close="$emit('close')" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">{{ title }}</h3>
        </template>

        <slot>
            <div class="mb-2" v-if="enrollment">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div><span class="text-muted">Plan: </span>{{ enrollment.plan_name }}</div>
                    <div><span class="text-muted">Balance: </span>{{ Number(enrollment.balance).toFixed(2) }} hours</div>
                </div>
            </div>

            <form class="mui-form" @submit.prevent="onSubmit()">
                <template v-if="mode == 'enroll'">
                    <form-input v-model="formData.client_accrual_plan" :errors="errors.client_accrual_plan" type="select" label="Plan" :options="planOptions" />
                    <form-input v-model="formData.start_date" :errors="errors.start_date" type="date" label="Start Date" />
                    <form-input v-model="formData.tenure_date" :errors="errors.tenure_date" type="date" label="Tenure Date" helper-text="Tiers, waiting periods and anniversary years count from this date. Blank for the hire date." />
                    <form-input v-model="formData.opening_balance" :errors="errors.opening_balance" type="decimal" label="Opening Balance" postfix="hours" :helper-text="openingBalanceHelp" />
                </template>

                <template v-if="mode == 'adjust'">
                    <form-input v-model="formData.hours" :errors="errors.hours" type="decimal" label="Hours" postfix="hours" helper-text="Negative hours reduce the balance." />
                    <form-input v-model="formData.effective_date" :errors="errors.effective_date" type="date" label="Effective Date" />
                    <form-input v-model="formData.reason" :errors="errors.reason" type="textarea" label="Reason" />
                </template>

                <template v-if="mode == 'tenure'">
                    <form-input v-model="formData.tenure_date" :errors="errors.tenure_date" type="date" label="Tenure Date" helper-text="Applies to paychecks from now on; past postings aren't recalculated." />
                </template>

                <template v-if="mode == 'switch'">
                    <p>The balance moves to the new plan, up to its balance limit. Hours over the limit are forfeited.</p>
                    <form-input v-model="formData.client_accrual_plan" :errors="errors.client_accrual_plan" type="select" label="New Plan" :options="switchPlanOptions" />
                    <form-input v-model="formData.switch_date" :errors="errors.switch_date" type="date" label="First Day on the New Plan" />
                </template>

                <template v-if="mode == 'end'">
                    <p>The remaining balance is forfeited. This cannot be undone.</p>
                    <form-input v-model="formData.end_date" :errors="errors.end_date" type="date" label="Last Day" />
                    <form-input v-model="formData.reason" :errors="errors.reason" type="textarea" label="Reason" />
                </template>
            </form>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button type="button" class="btn btn-outline-primary" @click.prevent="close()">Cancel</button>
                <button type="button" class="btn btn-primary" @click.prevent="onSubmit()">Save</button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'

// One modal per enrollment action; `mode` is enroll, adjust, tenure, switch or end.
export default {
    props: ['mode', 'client', 'employee', 'enrollment', 'availablePlans', 'defaultTenureDate', ],
    mixins: [FormMixin, ModalMixin],
    emits: ['close', 'updated', ],
    data() {
        return {
            formData: this.makeFormData(),
        }
    },
    computed: {
        title() {
            return {
                enroll: 'Enroll in Time Off Plan',
                adjust: 'Adjust Balance',
                tenure: 'Change Tenure Date',
                switch: 'Switch Plan',
                end: 'End Enrollment',
            }[this.mode]
        },
        planOptions() {
            return this.availablePlans.map(p => ({text: `${p.name} (${p.plan_type_pretty})`, value: p.id}))
        },
        switchPlanOptions() {
            return this.planOptions.filter(o => o.value != this.enrollment.client_accrual_plan_id)
        },
        selectedPlan() {
            return this.availablePlans.find(p => p.id == this.formData.client_accrual_plan)
        },
        openingBalanceHelp() {
            if (this.selectedPlan && this.selectedPlan.method == 'set-balance') {
                return "This year's hours. Later years are granted on the first paycheck of each accrual year."
            }
            return 'Hours carried over from before this enrollment, if any.'
        },
        url() {
            const base = `/clients/${this.client.id}/employees/${this.employee.id}/accrual-plans`
            return {
                enroll: base,
                adjust: `${base}/${this.enrollment?.id}/adjustments`,
                tenure: `${base}/${this.enrollment?.id}`,
                switch: `${base}/${this.enrollment?.id}/switch`,
                end: `${base}/${this.enrollment?.id}/end`,
            }[this.mode]
        },
    },
    methods: {
        makeFormData() {
            return {
                enroll: {client_accrual_plan: '', start_date: '', tenure_date: this.defaultTenureDate || '', opening_balance: ''},
                adjust: {hours: '', effective_date: '', reason: ''},
                tenure: {tenure_date: this.enrollment?.tenure_date || ''},
                switch: {client_accrual_plan: '', switch_date: ''},
                end: {end_date: '', reason: ''},
            }[this.mode]
        },
        onSubmit() {
            const data = this.getFormData()
            const request = this.mode == 'tenure' ? this.$api.put(this.url, data) : this.$api.post(this.url, data)

            this.$store.dispatch('START_LOADING')
            return request.then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        },
    },
}
</script>
